import { useState, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useGetEnrollmentJointVerifyPhoneData, useSubmitEnrollmentJointVerifyPhoneOTPData } from '@egw/api'
import { Alert, AlertType, GenericError, OtpInput, ProgressBar, Tag } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentJointLayout } from '@egw/layouts'
import { sendEvent } from '@egw/events'
import { CheckIcon } from '@heroicons/react/24/outline'
import { useRouter } from '@egw/router'
import { WEALTH_ROUTES } from '@egw/constants'
import { UserIcon } from '@heroicons/react/20/solid'
import { useValidateRoute } from '../../hooks/navigation/validate-route'
import { useHandleEnrollmentErrors } from '../../utils/joint/api-errors/enrollment-error-handler'
import { EDataLayerEventWealth } from '../../constants/events/EDataLayerEventWealth'
import { formatPhoneNumber } from '../../utils/phone-number/phone-number'
import { PageHeader } from '../page-header/page-header'
import { newPathIsDifferent } from '../../utils/general/new-path-is-different'
import { ROUTES } from '../../constants/routes/routes-generic'
import { ROUTES as JOINT_ROUTES } from '../../constants/routes/routes-joint'
import { JOINT_ENROLLMENT_ERRORS } from '../../constants/api/error-types-joint'

// TODO: Make api requests here generic to reuse in joint and multi

const log = new Logger('Enrollment - Joint - VerifyPhone')

export const VerifyPhoneOtpJoint: FC = () => {
  const { t } = useTranslation('features-enrollment')

  const { navigate } = useRouter()
  const [codeError, setCodeError] = useState('')

  const { data, isLoading, error } = useGetEnrollmentJointVerifyPhoneData({})
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointVerifyPhoneOTPData()

  const [codeResent, setCodeResent] = useState(false)
  const [counter, setCounter] = useState(0)
  const [otpValue, setOtpValue] = useState('')

  const handleOtpComplete = (otp: string): void => {
    void onSubmit(otp)
  }

  const handleOtpChange = (value: string): void => {
    const trimmedValue = value.slice(0, 6)
    setOtpValue(trimmedValue)
  }

  const phoneNumber =
    !data?.isError && data?.activePerson?.phone?.phoneNumber ? data.activePerson.phone.phoneNumber : ''
  const applicationId = !data?.isError && data?.activePerson?.applicationId ? data.activePerson.applicationId : ''

  // Protect route
  useValidateRoute(data?.activePerson?.path)

  // Error handler for enrollment errors
  const handleEnrollmentErrors = useHandleEnrollmentErrors('Joint - VerifyPhoneOtp')

  const resendOtp = async (): Promise<void> => {
    setCodeResent(true)

    try {
      const response = await mutate({
        activePerson: {
          triggerPhoneOtp: true,
        },
      })

      // Check for API errors in the response
      if (response && handleEnrollmentErrors(response)) {
        return
      }

      sendEvent(EDataLayerEventWealth.BANKHYCHYS_PHONEOTPRESEND_SUBMIT, {
        applicationId,
      })

      if (response && !response.isError) {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_PHONE_VERIFIED)
      }

      setTimeout(() => {
        setCounter(30)
        const intervalId = setInterval(() => {
          setCounter((prev) => {
            if (prev <= 1) {
              clearInterval(intervalId)
              setCodeResent(false)

              return 0
            }
            return prev - 1
          })
        }, 1000)
      }, 5000)
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  const onSubmit = async (otpCode: string): Promise<void> => {
    if (otpCode && otpCode.length === 6) {
      setCodeError('')
      try {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_ENROLLMENT_STARTED)
        const response = await mutate({
          activePerson: {
            phoneOtpCode: otpCode,
          },
        })

        // Check for API errors in the response
        if (response && handleEnrollmentErrors(response)) {
          return
        }

        // If invalid token, set error
        if (response?.isError) {
          if (response.errors.includes(JOINT_ENROLLMENT_ERRORS.INVALID_TOKEN)) {
            setCodeError(t('common.verify_phone_v2.otp.code_expired'))
          }
        } else if (response?.activePerson?.path && newPathIsDifferent(location.pathname, response.activePerson.path)) {
          navigate(response.activePerson.path)
        } else if (!response?.activePerson?.path) {
          // If no path from BE, redirect to error
          navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
        }
      } catch (e) {
        log.error('Error submitting', e)
      }
    }
  }

  const progress = data?.activePerson?.progress
  const profileName = data?.activePerson?.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''

  return (
    <EnrollmentJointLayout title={t('common.verify_phone_v2.otp.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {profileName ? (
            <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
              {profileName}
            </Tag>
          ) : null}
          <PageHeader
            title={t('common.verify_phone_v2.otp.title')}
            backPath={`${WEALTH_ROUTES.ENROLLMENT.path}/${JOINT_ROUTES.VERIFY_EMAIL.path}`}
          />
          <ProgressBar showAnimation progressPercentage={progress} className="mb-xl" />
          <p className="typo-primary-base-regular text-text-meta">{t('common.verify_phone_v2.otp.sent_to')}</p>
          <p className="typo-primary-base-semibold text-text-header mb-md fs-exclude">
            {formatPhoneNumber(phoneNumber)}
          </p>

          <OtpInput
            value={otpValue}
            onChange={handleOtpChange}
            onComplete={handleOtpComplete}
            className="mb-sm"
            testId="otp-input"
            error={codeError}
          />

          <div className="flex items-baseline gap-4">
            <button
              onClick={() => void resendOtp()}
              disabled={codeResent || isSubmitting}
              className={`typo-primary-base-semibold text-left w-fit ${codeResent ? 'text-text-disabled' : ' text-text-action'}`}
              type="button"
            >
              {t('common.verify_phone_v2.otp.resend_message')}
            </button>
            {codeResent && !counter ? (
              <p className="typo-primary-base-semibold text-text-header-success flex items-center gap-2">
                {t('common.verify_phone_v2.otp.message_sent')} <CheckIcon className="size-5" />
              </p>
            ) : null}
            {counter ? (
              <p className="typo-primary-base-semibold text-text-meta">
                {t('common.verify_phone_v2.otp.send_another_message', {
                  time: counter,
                })}
              </p>
            ) : null}
          </div>

          <div className="flex-1 md:hidden" />

          {submitError ? (
            <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
          ) : null}

          {/* AUTO GENERATED COMPONENTS */}
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
